import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Mail, Phone, MapPin, User, Building2, Code2, Tag, FileText, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Reveal, Magnetic } from "./motion";
import { SERVICE_OPTIONS, BUDGET_OPTIONS, REGISTERED_ADDRESS, PRIMARY_EMAIL, PRIMARY_PHONE } from "../data";
import api from "../lib/api";
import { captureCrmLead } from "../lib/crm";

const initial = { name: "", company: "", email: "", phone: "", service: "", budget: "", message: "" };

export const Contact = ({ defaultService = "", hideEyebrow = false }) => {
  const location = useLocation();
  const isHome = location.pathname === "/" || location.pathname === "";
  const shouldHideEyebrow = hideEyebrow || isHome || location.pathname === "/contact";
  const [form, setForm] = useState({ ...initial, service: defaultService });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [errors, setErrors] = useState({});
  const [settings, setSettings] = useState({
    registered_address: REGISTERED_ADDRESS,
    email: PRIMARY_EMAIL,
    phone: PRIMARY_PHONE,
    whatsapp: PRIMARY_PHONE
  });

  useEffect(() => {
    api.get("/settings")
      .then((res) => {
        if (res.data) setSettings(res.data);
      })
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const errs = {};
    const nameTrim = (form.name || "").trim();
    if (!nameTrim) {
      errs.name = "Full name is required.";
    } else if (nameTrim.length < 2) {
      errs.name = "Name must be at least 2 characters.";
    } else if (!/[a-zA-Z]/.test(nameTrim)) {
      errs.name = "Please enter a valid name with letters.";
    }

    const emailTrim = (form.email || "").trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
    if (!emailTrim) {
      errs.email = "Email address is required.";
    } else if (!emailRegex.test(emailTrim)) {
      errs.email = "Please enter a valid email address (e.g. name@company.com).";
    }

    const phoneTrim = (form.phone || "").trim();
    if (!phoneTrim) {
      errs.phone = "Phone number is required.";
    } else {
      const digits = phoneTrim.replace(/[^0-9]/g, "");
      if (digits.length !== 10) {
        errs.phone = "Please enter a valid 10-digit phone number";
      }
    }

    const companyTrim = (form.company || "").trim();
    if (!companyTrim) {
      errs.company = "Company name is required.";
    }

    if (!form.service) {
      errs.service = "Please select a service.";
    }

    if (!form.budget) {
      errs.budget = "Please select a budget range.";
    }

    const msgTrim = (form.message || "").trim();
    if (!msgTrim) {
      errs.message = "Project details / message is required.";
    } else if (msgTrim.length < 10) {
      errs.message = "Please describe your project (minimum 10 characters).";
    }

    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      const firstError = Object.values(validationErrors)[0];
      toast.error(firstError || "Please fill the required fields in the correct format.");
      return;
    }
    setErrors({});
    setLoading(true);
    const applicantName = form.name.trim();

    try {
      // 1. Direct CRM webhook capture (non-blocking, direct delivery)
      captureCrmLead({
        name: applicantName,
        email: form.email.trim(),
        phone: form.phone.trim(),
        service: form.service || "General Inquiry",
        notes: form.message.trim(),
        company: form.company.trim(),
        budget: form.budget,
      });

      // 2. Backend API persistence & notifications
      try {
        await api.post("/contact", {
          name: applicantName,
          email: form.email.trim(),
          phone: form.phone.trim(),
          service: form.service || "General Inquiry",
          message: form.message.trim(),
          company: form.company.trim(),
          budget: form.budget,
        });
      } catch (err) {
        console.warn("Backend API contact notification:", err);
      }

      // 3. Thank you message popup and toast
      setSubmittedName(applicantName);
      setSubmitted(true);
      toast.success("Thanks! We'll be in touch within 24 hours.");
      setForm({ ...initial, service: defaultService });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const getInputCls = (hasError) =>
    `w-full bg-white/85 border ${
      hasError
        ? "border-red-500/80 ring-2 ring-red-500/15"
        : "border-black/10 focus:border-[#FF5A1F] focus:ring-2 focus:ring-[#FF5A1F]/15"
    } rounded-xl pl-10 pr-4 py-3.5 text-sm outline-none focus:bg-white transition-all placeholder:text-neutral-400 text-[#0B0B0B] shadow-sm`;

  const cleanPhone = (settings.whatsapp || PRIMARY_PHONE).replace(/[^0-9]/g, "");

  return (
    <section id="contact" className="relative py-12 sm:py-16 md:py-20 px-5 sm:px-8 scroll-mt-20 overflow-hidden bg-gradient-to-br from-[#FFFDF9] via-[#FFF9F3] to-[#FFF4EC]">
      {/* Background earth map & ethereal warm light overlay */}
      <div className="absolute inset-0 pointer-events-none select-none overflow-hidden">
        <img
          src="/brand/contact-bg.png"
          alt=""
          className="w-full h-full object-cover object-center opacity-85 mix-blend-multiply"
        />
        <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#FF5A1F]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -right-20 w-[30rem] h-[30rem] bg-[#FF5A1F]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 left-1/4 w-[28rem] h-[28rem] bg-orange-400/10 rounded-full blur-3xl pointer-events-none" />
      </div>

      <div className="relative max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-14 items-center">
          <div>
            {!shouldHideEyebrow && (
              <Reveal>
                <div className="flex items-center gap-2.5 mb-4">
                  <span className="w-7 h-[2px] bg-[#FF5A1F] rounded-full inline-block" />
                  <span className="text-[#FF5A1F] font-heading text-xs sm:text-sm font-bold uppercase tracking-[0.22em]">
                    Let's talk
                  </span>
                </div>
              </Reveal>
            )}

            <Reveal delay={0.1}>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight leading-[1.12] text-[#0B0B0C]">
                Start your next<br />
                <span className="text-[#FF5A1F]">breakthrough</span>
              </h2>
            </Reveal>

            <Reveal delay={0.2}>
              <p className="mt-4 text-base sm:text-lg text-neutral-500 leading-relaxed max-w-md">
                Tell us about your project. We'll get back within one business day with a plan to move forward.
              </p>
            </Reveal>
            
            <div className="mt-8 space-y-4">
              <Reveal delay={0.3}>
                <div className="flex flex-col gap-3.5">
                  <a href={`mailto:${settings.email || PRIMARY_EMAIL}`} className="group flex items-center gap-3.5 hover:translate-x-1 transition-transform">
                    <div className="w-10 h-10 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 flex items-center justify-center shrink-0 text-[#FF5A1F] group-hover:bg-[#FF5A1F] group-hover:text-white transition-colors shadow-sm">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="block text-[11px] font-bold uppercase tracking-wider text-neutral-400">EMAIL</span>
                      <span className="block text-sm font-semibold text-[#0B0B0C] group-hover:text-[#FF5A1F] transition-colors">{settings.email || PRIMARY_EMAIL}</span>
                    </div>
                  </a>

                  <a href={`tel:${settings.phone || PRIMARY_PHONE}`} className="group flex items-center gap-3.5 hover:translate-x-1 transition-transform">
                    <div className="w-10 h-10 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 flex items-center justify-center shrink-0 text-[#FF5A1F] group-hover:bg-[#FF5A1F] group-hover:text-white transition-colors shadow-sm">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div>
                      <span className="block text-[11px] font-bold uppercase tracking-wider text-neutral-400">PHONE</span>
                      <span className="block text-sm font-semibold text-[#0B0B0C] group-hover:text-[#FF5A1F] transition-colors">{settings.phone || PRIMARY_PHONE}</span>
                    </div>
                  </a>

                  <a href={`https://wa.me/${cleanPhone}`} target="_blank" rel="noreferrer" className="group flex items-center gap-3.5 hover:translate-x-1 transition-transform">
                    <div className="w-10 h-10 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 flex items-center justify-center shrink-0 text-[#FF5A1F] group-hover:bg-[#FF5A1F] group-hover:text-white transition-colors shadow-sm">
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                      </svg>
                    </div>
                    <div>
                      <span className="block text-[11px] font-bold uppercase tracking-wider text-neutral-400">WHATSAPP</span>
                      <span className="block text-sm font-semibold text-[#0B0B0C] group-hover:text-[#FF5A1F] transition-colors">{settings.phone || PRIMARY_PHONE}</span>
                    </div>
                  </a>
                </div>
              </Reveal>

              <Reveal delay={0.4}>
                <div className="pt-3 border-t border-black/5 flex items-start gap-3.5">
                  <div className="w-10 h-10 rounded-full bg-[#FF5A1F]/10 border border-[#FF5A1F]/20 flex items-center justify-center shrink-0 text-[#FF5A1F] shadow-sm mt-0.5">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="font-bold text-[11px] uppercase tracking-wider text-[#FF5A1F] block">Registered Office</span>
                    <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed mt-0.5 max-w-sm">{settings.registered_address || REGISTERED_ADDRESS}</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.2}>
            <form onSubmit={submit} data-testid="contact-form" className="relative rounded-3xl p-6 sm:p-8 md:p-9 bg-white/75 backdrop-blur-xl border border-white/80 shadow-[0_20px_50px_rgba(255,90,31,0.06),0_10px_25px_rgba(0,0,0,0.03)] space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="relative">
                  <User className="absolute left-3.5 top-3.5 w-4 h-4 text-neutral-400 pointer-events-none" />
                  <input
                    data-testid="contact-name"
                    type="text"
                    className={getInputCls(errors.name)}
                    placeholder="Full name *"
                    value={form.name}
                    onChange={(e) => {
                      set("name", e.target.value.slice(0, 80));
                      if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                    }}
                  />
                  {errors.name && <p className="text-[11px] sm:text-xs text-red-500 mt-1 pl-1 font-medium">{errors.name}</p>}
                </div>
                <div className="relative">
                  <Building2 className="absolute left-3.5 top-3.5 w-4 h-4 text-neutral-400 pointer-events-none" />
                  <input
                    data-testid="contact-company"
                    type="text"
                    className={getInputCls(!!errors.company)}
                    placeholder="Company *"
                    value={form.company}
                    onChange={(e) => {
                      set("company", e.target.value.slice(0, 100));
                      if (errors.company) setErrors((prev) => ({ ...prev, company: undefined }));
                    }}
                  />
                  {errors.company && <p className="text-[11px] sm:text-xs text-red-500 mt-1 pl-1 font-medium">{errors.company}</p>}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="relative">
                  <Mail className="absolute left-3.5 top-3.5 w-4 h-4 text-neutral-400 pointer-events-none" />
                  <input
                    data-testid="contact-email"
                    type="email"
                    className={getInputCls(errors.email)}
                    placeholder="Email *"
                    value={form.email}
                    onChange={(e) => {
                      set("email", e.target.value.slice(0, 100));
                      if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                    }}
                  />
                  {errors.email && <p className="text-[11px] sm:text-xs text-red-500 mt-1 pl-1 font-medium">{errors.email}</p>}
                </div>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-3.5 w-4 h-4 text-neutral-400 pointer-events-none" />
                  <input
                    data-testid="contact-phone"
                    type="tel"
                    className={getInputCls(errors.phone)}
                    placeholder="Phone * (e.g. +91 98765 43210)"
                    value={form.phone}
                    onChange={(e) => {
                      // Restrict characters: only allow digits, +, spaces, hyphens, parentheses
                      const val = e.target.value.replace(/[^0-9+\s\-()]/g, "").slice(0, 20);
                      set("phone", val);
                      if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
                    }}
                  />
                  {errors.phone && <p className="text-[11px] sm:text-xs text-red-500 mt-1 pl-1 font-medium">{errors.phone}</p>}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="relative">
                  <Code2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none z-10" />
                  <Select value={form.service} onValueChange={(v) => { set("service", v); if (errors.service) setErrors((prev) => ({ ...prev, service: undefined })); }}>
                    <SelectTrigger data-testid="contact-service" className={getInputCls(!!errors.service) + " h-auto flex items-center justify-between"}><SelectValue placeholder="Service *" /></SelectTrigger>
                    <SelectContent>{SERVICE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  {errors.service && <p className="text-[11px] sm:text-xs text-red-500 mt-1 pl-1 font-medium">{errors.service}</p>}
                </div>
                <div className="relative">
                  <Tag className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none z-10" />
                  <Select value={form.budget} onValueChange={(v) => { set("budget", v); if (errors.budget) setErrors((prev) => ({ ...prev, budget: undefined })); }}>
                    <SelectTrigger data-testid="contact-budget" className={getInputCls(!!errors.budget) + " h-auto flex items-center justify-between"}><SelectValue placeholder="Budget *" /></SelectTrigger>
                    <SelectContent>{BUDGET_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                  {errors.budget && <p className="text-[11px] sm:text-xs text-red-500 mt-1 pl-1 font-medium">{errors.budget}</p>}
                </div>
              </div>
              <div className="relative">
                <FileText className="absolute left-3.5 top-3.5 w-4 h-4 text-neutral-400 pointer-events-none" />
                <textarea
                  data-testid="contact-message"
                  rows={4}
                  className={getInputCls(errors.message) + " pt-3 resize-none"}
                  placeholder="Tell us about your project *"
                  value={form.message}
                  onChange={(e) => {
                    set("message", e.target.value.slice(0, 2000));
                    if (errors.message) setErrors((prev) => ({ ...prev, message: undefined }));
                  }}
                />
                {errors.message && <p className="text-[11px] sm:text-xs text-red-500 mt-1 pl-1 font-medium">{errors.message}</p>}
              </div>
              <Magnetic className="w-full">
                <button data-testid="contact-submit" disabled={loading} className="group w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-2xl bg-gradient-to-r from-[#FF5A1F] to-[#FF6E2B] text-white font-semibold text-base shadow-[0_10px_25px_rgba(255,90,31,0.35)] hover:shadow-[0_12px_30px_rgba(255,90,31,0.45)] hover:from-[#ff6429] hover:to-[#ff7b3d] transition-all disabled:opacity-60">
                  {loading ? "Sending..." : "Submit Inquiry"}{!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
                </button>
              </Magnetic>
            </form>
          </Reveal>
        </div>
      </div>
      {/* Thank You Success Popup Modal */}
      <AnimatePresence>
        {submitted && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSubmitted(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            />

            {/* Modal Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.4 }}
              className="relative w-full max-w-lg bg-white rounded-3xl p-7 sm:p-9 shadow-2xl z-10 my-auto border border-black/10 overflow-hidden text-center"
            >
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#FF5A1F]/10 rounded-full blur-3xl pointer-events-none" />

              <button
                onClick={() => setSubmitted(false)}
                className="absolute top-5 right-5 w-10 h-10 rounded-full bg-neutral-100 grid place-items-center text-neutral-500 hover:text-black hover:bg-neutral-200 transition-colors z-20"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 grid place-items-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10" />
              </div>

              <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#0B0B0B]">Thank You!</h3>
              <p className="text-neutral-600 max-w-md mx-auto text-sm sm:text-base leading-relaxed mt-2.5">
                Thank you{submittedName ? ", " : ""}<span className="font-semibold text-neutral-800">{submittedName}</span>. Your inquiry has been submitted successfully. Our team will review your requirements and get back to you within <span className="font-semibold text-[#FF5A1F]">24 hours</span>.
              </p>

              <div className="pt-6">
                <button
                  onClick={() => setSubmitted(false)}
                  className="px-8 py-3.5 rounded-full bg-[#FF5A1F] text-white font-semibold text-sm hover:bg-[#ff6d38] transition-colors shadow-lg shadow-[#FF5A1F]/25 hover:shadow-xl hover:shadow-[#FF5A1F]/35"
                >
                  Got it, thank you!
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Contact;

